// WYSIWYG rich-text editor for task descriptions + comments.
//
// A contentEditable surface with a compact format bar (core buttons inline +
// an overflow menu, per the design review), @mention autocomplete, and inline
// image paste/drop. Emits sanitized-on-save HTML via onChange.
//
// Design decisions (see plan-design-review):
//   - Bar shown while editing, reuses .btn-ghost styling, tan-tint active.
//   - Core inline: Bold · Italic · Highlight · Color ; overflow …: Underline ·
//     Strike · Size · Clear. 44px touch targets, scrolls/menus on phone.
//   - Color = 6 brand swatches; Size = 小/中/大. No arbitrary colors/fonts.
//
// Formatting uses execCommand (deprecated but universally supported); it's the
// pragmatic choice for a simple bar. Bold/italic/underline/strike emit tags
// (styleWithCSS off); color/highlight/size emit inline style which the render
// sanitizer constrains to color/background-color/font-size only.
//
// Images: inserted as <img data-attachment-id> (no src). While editing we set
// the DOM src to an authed blob so it shows; readHtml() strips that src so only
// the id is ever saved (blob URLs are ephemeral). The renderer re-resolves the
// authed blob on display.

import { useRef, useEffect, useState, useCallback } from 'react';
import * as api from '../lib/api.ts';
import { Icon } from './Icon.jsx';
import { findMentionQuery } from './MentionTextarea.jsx';
import { useIsMobile } from '../lib/useMediaQuery.js';

// Highlight palette (≥3, soft on-brand tones). Re-applying the same color over
// an already-highlighted selection toggles it off (Word-style).
const HILITES = [
  { key: 'y', label: '黄', css: '#fde9a6' },
  { key: 'g', label: '绿', css: '#cdeecb' },
  { key: 'p', label: '粉', css: '#fcd5e5' },
  { key: 'b', label: '蓝', css: '#cfe6fb' },
];
const COLORS = [
  { key: 'default', label: '默认', css: '#333333' },
  { key: 'red',     label: '红',   css: '#dd3327' },
  { key: 'amber',   label: '琥珀', css: '#874b00' },
  { key: 'green',   label: '绿',   css: '#3d8b5e' },
  { key: 'tan',     label: '茶',   css: '#9c8574' },
  { key: 'gray',    label: '灰',   css: '#777777' },
];
const FONTS = [
  { v: 'inherit', l: '默认' },
  { v: 'system-ui, sans-serif', l: '系统' },
  { v: '"PingFang SC","Microsoft YaHei",sans-serif', l: '黑体' },
  { v: '"Songti SC","SimSun",serif', l: '宋体' },
  { v: 'Georgia, "Times New Roman", serif', l: 'Serif' },
  { v: 'ui-monospace, Menlo, monospace', l: '等宽' },
];
const SIZE_OPTIONS = [
  { v: '12px', l: '12' },
  { v: '14px', l: '14' },
  { v: '16px', l: '16' },
  { v: '18px', l: '18' },
  { v: '20px', l: '20' },
  { v: '24px', l: '24' },
  { v: '28px', l: '28' },
];

function exec(cmd, value) {
  document.execCommand(cmd, false, value);
}

// Normalize #rgb / #rrggbb / rgb(...) to "r,g,b" so we can compare a chosen
// highlight color against the selection's current background (for toggle-off).
function normColor(c) {
  if (!c) return '';
  c = String(c).trim().toLowerCase();
  if (c === 'transparent' || c === 'rgba(0, 0, 0, 0)') return '';
  if (c[0] === '#') {
    let h = c.slice(1);
    if (h.length === 3) h = h.split('').map(x => x + x).join('');
    const n = parseInt(h, 16);
    return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  }
  const m = c.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  return m ? `${+m[1]},${+m[2]},${+m[3]}` : c;
}
function colorsMatch(a, b) { return normColor(a) !== '' && normColor(a) === normColor(b); }
function currentBg() {
  try { return document.queryCommandValue('backColor') || document.queryCommandValue('hiliteColor') || ''; }
  catch { return ''; }
}

export const RichTextEditor = ({
  value, onChange, staff = [], taskId,
  placeholder = '', rows = 4, onKeyDown, autoFocus = false, compact = false
}) => {
  const boxRef = useRef(null);
  const fileRef = useRef(null);
  const [active, setActive] = useState({});       // {bold, italic, underline, strike}
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [hlOpen, setHlOpen] = useState(false);
  const isMobile = useIsMobile();
  const [uploading, setUploading] = useState(false);
  const [mentions, setMentions] = useState(null);  // {items, range, query} | null
  const [mentionAt, setMentionAt] = useState({ top: 0, left: 0 });
  const [mentionIdx, setMentionIdx] = useState(0);
  const savedRange = useRef(null);

  // Seed initial HTML once (and when the external value changes while not
  // focused — e.g. switching tasks). Never overwrite while the user is typing.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (document.activeElement === el) {
      // While focused we don't clobber typing, but an external CLEAR (value reset
      // to empty after a send) must still empty the box — an empty value is
      // unambiguously a reset, never an echo of what was typed.
      if (!value && el.innerHTML !== '') el.innerHTML = '';
      return;
    }
    if (el.innerHTML !== (value || '')) el.innerHTML = value || '';
    resolveImages();
    if (autoFocus) placeCaretEnd(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Resolve authed inline images to blob URLs for display (editor side).
  const blobUrls = useRef([]);
  function resolveImages() {
    const el = boxRef.current;
    if (!el) return;
    el.querySelectorAll('img[data-attachment-id]').forEach(img => {
      if (img.getAttribute('src')) return;
      const id = img.getAttribute('data-attachment-id');
      if (!id) return;
      img.setAttribute('contenteditable', 'false');
      api.fetchAttachmentBlobUrl(id).then(u => { blobUrls.current.push(u); img.src = u; }).catch(() => {});
    });
  }
  useEffect(() => () => { blobUrls.current.forEach(u => URL.revokeObjectURL(u)); }, []);

  // Read editor HTML for onChange/save: clone, strip ephemeral blob src from
  // attachment images and the editing-only contenteditable attr.
  const readHtml = useCallback(() => {
    const el = boxRef.current;
    if (!el) return '';
    const clone = el.cloneNode(true);
    clone.querySelectorAll('img[data-attachment-id]').forEach(img => img.removeAttribute('src'));
    clone.querySelectorAll('[contenteditable]').forEach(n => n.removeAttribute('contenteditable'));
    return clone.innerHTML;
  }, []);

  const emit = useCallback(() => { onChange?.(readHtml()); }, [onChange, readHtml]);

  // Active-state tracking for the toggle buttons.
  const refreshActive = useCallback(() => {
    const el = boxRef.current;
    if (!el || document.activeElement !== el) return;
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strike: document.queryCommandState('strikeThrough'),
      });
    } catch { /* queryCommandState can throw in some states */ }
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  function focusBox() { boxRef.current?.focus(); }

  function runCmd(cmd) {
    focusBox();
    exec('styleWithCSS', false);
    exec(cmd);
    refreshActive();
    emit();
  }
  function applyHighlight(css) {
    focusBox();
    restoreRange();
    exec('styleWithCSS', true);
    if (colorsMatch(currentBg(), css)) {
      exec('hiliteColor', 'transparent');   // same color again → toggle off (Word-style)
      exec('backColor', 'transparent');
    } else {
      exec('hiliteColor', css);
      exec('backColor', css);
    }
    setHlOpen(false);
    emit();
  }
  function runColor(css) {
    focusBox();
    restoreRange();
    exec('styleWithCSS', true);
    exec('foreColor', css);
    setColorOpen(false);
    emit();
  }
  // Apply an exact px font size. execCommand('fontSize') only accepts the legacy
  // 1-7 scale, so we mark the selection with size 7 (→ <font size="7">) then
  // rewrite each into <span style="font-size:Npx">, which the sanitizer keeps
  // (it strips <font> but allows span + font-size).
  function applyFontSize(px) {
    if (!px) return;
    focusBox();
    restoreRange();
    exec('styleWithCSS', false);
    exec('fontSize', '7');
    const box = boxRef.current;
    if (box) {
      box.querySelectorAll('font[size="7"]').forEach(f => {
        const span = document.createElement('span');
        span.style.fontSize = px;
        while (f.firstChild) span.appendChild(f.firstChild);
        f.replaceWith(span);
      });
    }
    emit();
  }
  function applyFont(face) {
    focusBox();
    restoreRange();
    exec('styleWithCSS', true);
    exec('fontName', face || 'inherit');   // → <span style="font-family:…"> (sanitizer keeps it)
    emit();
  }
  function runUndo() {
    focusBox();
    exec('undo');
    refreshActive();
    emit();
  }
  function runRedo() {
    focusBox();
    exec('redo');
    refreshActive();
    emit();
  }

  // Popovers can steal focus/selection; stash the range when opening one.
  function stashRange() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRange.current = sel.getRangeAt(0).cloneRange();
  }
  function restoreRange() {
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  }

  // ---- @mention autocomplete -------------------------------------------------
  const activeStaff = (staff || []).filter(s => s.active && s.role !== 'system' && s.name);

  function updateMentionUI() {
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || !sel.anchorNode || sel.anchorNode.nodeType !== 3) {
      setMentions(null); return;
    }
    const node = sel.anchorNode;
    const offset = sel.anchorOffset;
    const q = findMentionQuery(node.textContent || '', offset);
    if (!q) { setMentions(null); return; }
    const filter = q.filter.toLowerCase();
    const items = activeStaff
      .filter(s => s.name.toLowerCase().includes(filter))
      .slice(0, 6);
    if (items.length === 0) { setMentions(null); return; }
    const range = document.createRange();
    range.setStart(node, q.start);
    range.setEnd(node, offset);
    const rect = range.getBoundingClientRect();
    setMentionAt({ top: rect.bottom + 4, left: rect.left });
    setMentionIdx(0);
    setMentions({ items, atNode: node, start: q.start, end: offset });
  }

  function pickMention(s) {
    const m = mentions;
    if (!m) return;
    const range = document.createRange();
    range.setStart(m.atNode, m.start);
    range.setEnd(m.atNode, m.end);
    range.deleteContents();
    const chip = document.createElement('span');
    chip.className = 'mention';
    chip.setAttribute('data-staff-id', s.id);
    chip.setAttribute('contenteditable', 'false');
    chip.textContent = '@' + s.name;
    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(chip);
    // caret after the space
    const sel = window.getSelection();
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    setMentions(null);
    emit();
  }

  // ---- images ----------------------------------------------------------------
  async function uploadAndInsert(file) {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 25 * 1024 * 1024) return;
    setUploading(true);
    try {
      const att = await api.uploadAttachment(taskId, file);
      focusBox();
      const img = document.createElement('img');
      img.setAttribute('data-attachment-id', att.id);
      img.setAttribute('alt', (file.name || '').replace(/[\[\]()]/g, ''));
      img.setAttribute('contenteditable', 'false');
      const sel = window.getSelection();
      const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      if (range && boxRef.current.contains(range.commonAncestorContainer)) {
        range.insertNode(img);
        range.setStartAfter(img); range.collapse(true);
        sel.removeAllRanges(); sel.addRange(range);
      } else {
        boxRef.current.appendChild(img);
      }
      const u = await api.fetchAttachmentBlobUrl(att.id);
      blobUrls.current.push(u); img.src = u;
      emit();
    } catch { /* surfaced by caller toast elsewhere */ }
    finally { setUploading(false); }
  }
  function onPaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) { e.preventDefault(); void uploadAndInsert(f); return; }
      }
    }
  }
  function onDrop(e) {
    const f = [...(e.dataTransfer?.files || [])].find(x => x.type.startsWith('image/'));
    if (f) { e.preventDefault(); void uploadAndInsert(f); }
  }

  function onInput() { emit(); updateMentionUI(); }

  function onKeyDownInner(e) {
    if (mentions) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentions.items.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => (i - 1 + mentions.items.length) % mentions.items.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentions.items[mentionIdx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMentions(null); return; }
    }
    onKeyDown?.(e);
  }

  return (
    <div className={`rte ${compact ? 'rte-compact' : ''}`} onDragOver={e => e.preventDefault()} onDrop={onDrop}>
      <div className="rte-bar" role="toolbar" aria-label="格式" onMouseDown={e => e.preventDefault()}>
        {/* Undo / Redo (Outlook puts these first) */}
        <button type="button" className="rte-btn" title="撤销" aria-label="撤销" onClick={runUndo}><Icon name="undo" /></button>
        <button type="button" className="rte-btn" title="重做" aria-label="重做" onClick={runRedo}><Icon name="redo" /></button>
        <span className="rte-sep" aria-hidden="true" />
        {/* Font family + size */}
        {!isMobile && (
          <select className="rte-font-select" aria-label="字体" title="字体"
                  onMouseDown={e => { e.stopPropagation(); stashRange(); }}
                  onChange={e => { applyFont(e.target.value); e.currentTarget.selectedIndex = 0; }}>
            <option value="">字体</option>
            {FONTS.map(f => <option key={f.l} value={f.v}>{f.l}</option>)}
          </select>
        )}
        {!isMobile && (
          <select className="rte-size-select" aria-label="字号" title="字号"
                  onMouseDown={e => { e.stopPropagation(); stashRange(); }}
                  onChange={e => { applyFontSize(e.target.value); e.currentTarget.selectedIndex = 0; }}>
            <option value="">字号</option>
            {SIZE_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        )}
        {/* Font color + highlight */}
        <button type="button" className="rte-btn" title="颜色" aria-label="颜色" aria-expanded={colorOpen} onClick={() => { stashRange(); setColorOpen(o => !o); setHlOpen(false); setOverflowOpen(false); }}><span className="rte-color-ind">A</span></button>
        <button type="button" className="rte-btn" title="高亮" aria-label="高亮" aria-expanded={hlOpen} onClick={() => { stashRange(); setHlOpen(o => !o); setColorOpen(false); setOverflowOpen(false); }}><Icon name="highlight" /></button>
        {/* Bold / Italic / Underline (+ Strike on desktop) */}
        <button type="button" className={`rte-btn${active.bold ? ' is-active' : ''}`} aria-pressed={!!active.bold} title="加粗" aria-label="加粗" onClick={() => runCmd('bold')}><b>B</b></button>
        <button type="button" className={`rte-btn${active.italic ? ' is-active' : ''}`} aria-pressed={!!active.italic} title="斜体" aria-label="斜体" onClick={() => runCmd('italic')}><i>I</i></button>
        {!isMobile && (
          <button type="button" className={`rte-btn${active.underline ? ' is-active' : ''}`} aria-pressed={!!active.underline} title="下划线" aria-label="下划线" onClick={() => runCmd('underline')}><u>U</u></button>
        )}
        {!isMobile && (
          <button type="button" className={`rte-btn${active.strike ? ' is-active' : ''}`} aria-pressed={!!active.strike} title="删除线" aria-label="删除线" onClick={() => runCmd('strikeThrough')}><s>S</s></button>
        )}
        <div className="rte-spacer" />
        {isMobile && (
          <button type="button" className="rte-btn rte-more" title="更多" aria-label="更多" aria-expanded={overflowOpen} onClick={() => { setOverflowOpen(o => !o); setColorOpen(false); setHlOpen(false); }}>⋯</button>
        )}

        {colorOpen && (
          <div className="rte-pop rte-pop-color" onMouseDown={e => e.preventDefault()}>
            {COLORS.map(c => (
              <button key={c.key} type="button" className="rte-swatch" title={c.label} aria-label={c.label}
                      style={{ background: c.css }} onClick={() => runColor(c.css)} />
            ))}
          </div>
        )}
        {hlOpen && (
          <div className="rte-pop rte-pop-hl" onMouseDown={e => e.preventDefault()}>
            {HILITES.map(h => (
              <button key={h.key} type="button" className="rte-hl-swatch" title={h.label} aria-label={'高亮 ' + h.label}
                      style={{ background: h.css }} onClick={() => applyHighlight(h.css)} />
            ))}
          </div>
        )}
        {isMobile && overflowOpen && (
          <div className="rte-pop rte-pop-more" onMouseDown={e => e.preventDefault()}>
            <select className="rte-font-select" aria-label="字体" title="字体"
                    onMouseDown={e => { e.stopPropagation(); stashRange(); }}
                    onChange={e => { applyFont(e.target.value); e.currentTarget.selectedIndex = 0; }}>
              <option value="">字体</option>
              {FONTS.map(f => <option key={f.l} value={f.v}>{f.l}</option>)}
            </select>
            <select className="rte-size-select" aria-label="字号" title="字号"
                    onMouseDown={e => { e.stopPropagation(); stashRange(); }}
                    onChange={e => { applyFontSize(e.target.value); e.currentTarget.selectedIndex = 0; }}>
              <option value="">字号</option>
              {SIZE_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <button type="button" className={`rte-btn${active.underline ? ' is-active' : ''}`} aria-pressed={!!active.underline} title="下划线" aria-label="下划线" onClick={() => runCmd('underline')}><u>U</u></button>
            <button type="button" className={`rte-btn${active.strike ? ' is-active' : ''}`} aria-pressed={!!active.strike} title="删除线" aria-label="删除线" onClick={() => runCmd('strikeThrough')}><s>S</s></button>
          </div>
        )}
      </div>

      <div
        ref={boxRef}
        className="rte-box"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        style={{ minHeight: rows * 22 }}
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDownInner}
        onBlur={emit}
      />

      {!compact && (
        <div className="rte-imgbtn-row">
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                 onChange={e => { const f = e.target.files?.[0]; if (f) void uploadAndInsert(f); e.target.value = ''; }} />
          <button type="button" className="btn btn-ghost btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Icon name="plus" /> {uploading ? '上传中…' : '插入图片'}
          </button>
        </div>
      )}

      {mentions && (
        <ul className="rte-mention-menu" style={{ top: mentionAt.top, left: mentionAt.left }} role="listbox">
          {mentions.items.map((s, i) => (
            <li key={s.id} role="option" aria-selected={i === mentionIdx}
                className={`rte-mention-item${i === mentionIdx ? ' is-active' : ''}`}
                onMouseDown={e => { e.preventDefault(); pickMention(s); }}>
              <span className="rte-mention-name">@{s.name}</span>
              {s.title && <span className="rte-mention-title">{s.title}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

function placeCaretEnd(el) {
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(r);
}
